import React, { useState, useRef } from 'react';
import { observer } from 'src/utils/mobx-react';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import {
    MultibetIframeWrapper,
    MultibetIframe,
    MultibetLoader,
    MultibetLoginRequiredWrapper,
    MultibetLoginMessage,
    MultibetLoginButton,
} from './MultibetWidget.style';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MultibetWidgetState } from './MultibetWidgetState';
import { useMultibetMessageHandler } from './hooks/useMultibetMessageHandler';
import { useCommon } from 'src/domains/common/Common';
import { MultibetState } from 'src/domains/sportsbook/betting/state/multibetState/MultibetState';
import {
    ContentConfirmationWrapper,
    ContentText,
    ErrorConflictWrapper,
} from 'src/domains/sportsbook/webview/components/youDecide/YouDecide.style';
import { IconCheckMark } from 'src/domains/sportsbook/webview/components/youDecide/IconCheckMark';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface MultibetWidgetPropsType {
    event: EventModel;
    isOpen: boolean;
}

export const MultibetWidget = observer('MultibetWidget', ({ event, isOpen }: MultibetWidgetPropsType) => {
    const common = useCommon();
    const { rabHeaderTitle, componentVariants } = ConfigComponents.get(common).config;
    const multibetState = MultibetState.get(common);
    const [state] = useState(() => new MultibetWidgetState(multibetState, common, event.id, event.name, event.sport));
    const [isLoading, setIsLoading] = useState(true);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const fixtureId = event.betBuilderId;
    const multibetWidgetConfig = common.features.multibetWidget;
    const isAuthorized = common.session.isAuthorized;

    // Setup message handler for widget communication
    useMultibetMessageHandler(iframeRef, {
        onAddToBetslip: state.onAddToBetslipInner,
        onNon200Response: state.onAuthenticationError,
    });

    const handleIframeLoad = (): void => {
        setIsLoading(false);
    };

    if (!isOpen || fixtureId === null || multibetWidgetConfig === null) {
        return null;
    }

    if (!isAuthorized) {
        return (
            <MultibetLoginRequiredWrapper data-test='multibet-widget-login-required'>
                <MultibetLoginMessage>
                    <I18n
                        langKey='sportsbook.multibet.login-required.message'
                        defaultText='Please log in to access the Multibet feature.'
                    />
                </MultibetLoginMessage>
                <MultibetLoginButton onClick={(): void => StarRouter.get(common).redirectToLogin()}>
                    <I18n
                        langKey='sportsbook.multibet.login-required.button'
                        defaultText='Log in'
                    />
                </MultibetLoginButton>
            </MultibetLoginRequiredWrapper>
        );
    }

    const iframeUrl = `${multibetWidgetConfig.baseUrl}/multibet?productName=${multibetWidgetConfig.productName}&fixtureId=${fixtureId}`;

    return (
        <MultibetIframeWrapper>
            {isLoading && (
                <MultibetLoader size={12}>
                    <I18n
                        langKey='sportsbook.multibet.loading'
                        defaultText='Loading Multibet...'
                        data-test='multibet-loader'
                    />
                </MultibetLoader>
            )}
            <MultibetIframe
                ref={iframeRef}
                src={iframeUrl}
                title={`Multibet Widget for Event ${fixtureId}`}
                onLoad={handleIframeLoad}
                data-test='multibet-widget-iframe'
                loading='lazy'
                allowFullScreen
                referrerPolicy='no-referrer-when-downgrade'
            />
            {state.error ? (
                <ErrorConflictWrapper
                    data-test='conflicts'
                    variant={componentVariants.sportsbookVariant}
                >
                    {state.error}
                </ErrorConflictWrapper>
            ) : null}
            {state.isConfirmationOpened ? (
                <ContentConfirmationWrapper data-test='confirmation'>
                    <IconCheckMark />
                    <ContentText>Your {rabHeaderTitle} bet has been successfully added to your betslip</ContentText>
                </ContentConfirmationWrapper>
            ) : null}
        </MultibetIframeWrapper>
    );
});
